<template>
  <div class="po-cfm-detail-page">
    <!-- 顶部导航栏 -->
    <div class="header-bar">
      <div class="header-left" @click="onBack">
        <van-icon name="arrow-left" size="20" color="#333333" style="font-weight:600;" />
      </div>
      <div class="header-title">{{ pageTitle }}</div>
      <div class="header-right"></div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content" v-if="mainRow && Object.keys(mainRow).length > 0">
      <!-- 单据信息卡片 -->
      <div class="info-card">
        <div class="card-title-row">
          <span class="order-no">{{ mainRow.IDNO }}</span>
          <van-tag
            v-if="mainRow.STFG === 'Y'"
            :type="getStfgType(mainRow.STFG)"
            size="small"
            plain
          >{{ getStfgText(mainRow.STFG) }}</van-tag>
          <van-tag
            v-if="mainRow.ISCHK === 'Y'"
            type="success"
            size="small"
          >已确认</van-tag>
          <van-tag
            v-else-if="mainRow.STFG === 'Y'"
            type="warning"
            size="small"
          >待确认</van-tag>
          <van-tag v-else type="default" size="small">未审核</van-tag>
        </div>

        <!-- 基本信息 -->
        <div class="info-section">
          <div class="section-label">基本信息</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">供应商</span>
              <span class="info-value">{{ mainRow.SUPNM || mainRow.VENDNM || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">制单日期</span>
              <span class="info-value">{{ mainRow.ORDDTTM || mainRow.ADDDTTM || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">制单人</span>
              <span class="info-value">{{ mainRow.ADDUSER || mainRow.USERNM || '-' }}</span>
            </div>
            <div class="info-item" v-if="mainRow.PRC">
              <span class="info-label">总价</span>
              <span class="info-value amount">{{ mainRow.AMT || mainRow.PRC || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- 确认信息 -->
        <div class="info-section" v-if="mainRow.ISCHK === 'Y'">
          <div class="section-label confirm-info">确认信息</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">确认时间</span>
              <span class="info-value">{{ mainRow.CFMDTTM || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">交货日期</span>
              <span class="info-value date-highlight">{{ mainRow.REPLYDTTM || '-' }}</span>
            </div>
            <div class="info-item info-item-full" v-if="mainRow.CFMRMK">
              <span class="info-label">确认备注</span>
              <span class="info-value remark">{{ mainRow.CFMRMK }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 明细列表 -->
      <div class="detail-section">
        <div class="detail-header">
          <span class="detail-title">采购明细</span>
          <span class="detail-count">共 {{ dtlData.length }} 项</span>
        </div>

        <div class="detail-list">
          <div
            v-for="(item, index) in dtlData"
            :key="index"
            class="detail-item"
          >
            <div class="item-header">
              <span class="item-index">{{ index + 1 }}</span>
              <span class="item-code">{{ item.MATNO }}</span>
              <span class="item-name">{{ item.MATNM }}</span>
            </div>
            <div class="item-body">
              <div class="item-row">
                <span class="row-label">规格</span>
                <span class="row-value">{{ item.MATDESC || '-' }}</span>
              </div>
              <div class="item-row">
                <span class="row-label">单位</span>
                <span class="row-value">{{ item.UNIT || '-' }}</span>
              </div>
              <div class="item-row">
                <span class="row-label">数量</span>
                <span class="row-value qty">{{ item.QTY }}</span>
              </div>
              <div class="item-row">
                <span class="row-label">单价</span>
                <span class="row-value">{{ item.TAXPRC || item.PRC || '-' }}</span>
              </div>
              <div class="item-row">
                <span class="row-label">金额</span>
                <span class="row-value amount">{{ item.AMT || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 附件区域 -->
      <div class="attach-section" v-if="hasAttachments">
        <div class="attach-header">
          <span class="attach-title">附件</span>
        </div>
        <div class="attach-list">
          <div
            v-if="mainRow.FILEPATH || mainRow.IMGPATH"
            class="attach-item"
            @click="previewImage(mainRow.FILEPATH || mainRow.IMGPATH)"
          >
            <div class="attach-preview">
              <van-image
                v-if="isImage(mainRow.FILEPATH || mainRow.IMGPATH)"
                :src="mainRow.FILEPATH || mainRow.IMGPATH"
                fit="cover"
                width="80"
                height="80"
              />
              <div v-else class="attach-pdf">
                <van-icon name="description" size="32" />
                <span>PDF文件</span>
              </div>
            </div>
            <span class="attach-name">合同附件</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div class="loading-state" v-else-if="!loadFinished">
      <van-loading type="spinner" size="40px" vertical>加载中...</van-loading>
    </div>

    <!-- 底部操作栏 -->
    <div class="footer-bar" v-if="loadFinished">
      <van-button
        v-if="mainRow.STFG === 'Y' && mainRow.ISCHK !== 'Y'"
        class="footer-btn footer-btn-confirm"
        type="primary"
        @click="showConfirmDialog"
      >
        确认
      </van-button>
      <van-button
        v-if="mainRow.STFG === 'Y' && mainRow.ISCHK === 'Y'"
        class="footer-btn footer-btn-cancel"
        type="warning"
        @click="handleCancelConfirm"
      >
        弃认
      </van-button>
    </div>

    <!-- 确认弹窗 -->
    <van-popup v-model="confirmDialogVisible" position="bottom" round :style="{ height: 'auto' }">
      <div class="confirm-popup">
        <div class="popup-header">
          <span class="popup-title">确认采购单</span>
          <van-icon name="cross" size="20" @click="confirmDialogVisible = false" />
        </div>

        <div class="popup-body">
          <div class="popup-form-item">
            <label class="popup-label"><span class="required">*</span>预计交货日期</label>
            <van-field
              v-model="confirmForm.REPLYDTTM"
              readonly
              placeholder="选择交货日期"
              @click="showDatePicker = true"
            />
          </div>

          <div class="popup-form-item">
            <label class="popup-label">确认备注</label>
            <van-field
              v-model="confirmForm.CFMRMK"
              type="textarea"
              placeholder="请输入备注信息"
              rows="3"
              show-word-limit
            />
          </div>
        </div>

        <div class="popup-footer">
          <van-button class="popup-btn popup-btn-cancel" @click="confirmDialogVisible = false">
            取消
          </van-button>
          <van-button class="popup-btn popup-btn-submit" type="primary" @click="submitConfirm">
            确认
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- 日期选择器 -->
    <van-popup v-model="showDatePicker" position="bottom">
      <van-datetime-picker
        v-model="currentDate"
        type="date"
        title="选择交货日期"
        :min-date="minDate"
        :max-date="maxDate"
        @confirm="onDateConfirm"
        @cancel="showDatePicker = false"
      />
    </van-popup>

    <!-- 图片预览 -->
    <van-image-preview
      v-model="previewVisible"
      :images="previewImages"
      :start-position="0"
    >
      <template v-scalar:cover>预览</template>
    </van-image-preview>
  </div>
</template>

<script>
import { getDate } from '@/api/tools'
import { getBaseData, getTabColOrData } from '@/api/user'

export default {
  name: 'PoCfmConfirm',
  data() {
    return {
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),
      v_usernm: this.$cookies.get('v_usernm'),
      v_url: 'xuper_auth/getTableRecMany',

      mainRow: {},
      dtlData: [],
      loadFinished: false,

      // 确认弹窗
      confirmDialogVisible: false,
      showDatePicker: false,
      currentDate: new Date(),
      minDate: new Date(2024, 0, 1),
      maxDate: new Date(2030, 11, 31),
      confirmForm: {
        REPLYDTTM: '',
        CFMRMK: ''
      },

      // 图片预览
      previewVisible: false,
      previewImages: [],

      pageMode: 'view' // view, edit, add
    }
  },
  computed: {
    pageTitle() {
      const idno = this.mainRow.IDNO || '采购确认单'
      return this.pageMode === 'view' ? `${idno} - 查看` : idno
    },
    hasAttachments() {
      return !!(this.mainRow.FILEPATH || this.mainRow.IMGPATH)
    }
  },
  mounted() {
    this.pageMode = this.$route.query.mode || 'view'
    this.loadData()
  },
  methods: {
    onBack() {
      this.$router.go(-1)
    },

    // 加载数据
    loadData() {
      const idseq = this.$route.query.IDSEQ
      if (!idseq) {
        this.loadFinished = true
        return
      }

      // 加载主表数据
      this.$axios({
        method: 'post',
        url: this.v_url,
        data: {
          p_table: 'V_po_ord',
          p_key: 'idseq',
          p_value: idseq
        }
      }).then(res => {
        if (res.code === '200' && res.result && res.result.length > 0) {
          this.mainRow = res.result[0]
          // 如果是查看模式且有确认信息，回填确认表单
          if (this.mainRow.REPLYDTTM) {
            this.confirmForm.REPLYDTTM = this.mainRow.REPLYDTTM
          }
          if (this.mainRow.CFMRMK) {
            this.confirmForm.CFMRMK = this.mainRow.CFMRMK
          }
        }
      })

      // 加载明细数据
      this.$axios({
        method: 'post',
        url: this.v_url,
        data: {
          p_table: 'V_po_orddtl',
          p_key: 'idseq',
          p_value: idseq
        }
      }).then(res => {
        if (res.code === '200') {
          this.dtlData = res.result || []
        }
        this.loadFinished = true
      })
    },

    // 显示确认弹窗
    showConfirmDialog() {
      this.confirmForm = {
        REPLYDTTM: this.mainRow.REPLYDTTM || getDate('day'),
        CFMRMK: this.mainRow.CFMRMK || ''
      }
      this.confirmDialogVisible = true
    },

    // 提交确认
    submitConfirm() {
      if (!this.confirmForm.REPLYDTTM) {
        this.$toast('请选择交货日期')
        return
      }

      this.$dialog.confirm({
        title: '提示',
        message: '确认前请先查看详细内容，要确认吗？'
      }).then(() => {
        let cond_ = " set ischk='Y',cfmdttm='" + getDate('full') +
          "',replydttm='" + this.confirmForm.REPLYDTTM +
          "',cfrmk='" + (this.confirmForm.CFMRMK || '') + "'"

        this.$axios({
          method: 'post',
          url: 'xuper_auth/updateRec',
          data: {
            p_key: 'idseq',
            p_value: this.mainRow.IDSEQ,
            p_table: 'PO_ORD',
            p_cond: cond_
          }
        }).then(res => {
          if (res.code === '200') {
            this.$toast.success('确认成功')
            this.confirmDialogVisible = false
            // 更新本地数据
            this.mainRow.ISCHK = 'Y'
            this.mainRow.CFMDTTM = getDate('full')
            this.mainRow.REPLYDTTM = this.confirmForm.REPLYDTTM
            this.mainRow.CFMRMK = this.confirmForm.CFMRMK
          } else {
            this.$toast.fail(res.result || '确认失败')
          }
        })
      }).catch(() => {})
    },

    // 弃认
    handleCancelConfirm() {
      this.$dialog.confirm({
        title: '提示',
        message: '确定要反确认此单据吗？'
      }).then(() => {
        this.$axios({
          method: 'post',
          url: 'xuper_auth/updateRec',
          data: {
            p_key: 'idseq',
            p_value: this.mainRow.IDSEQ,
            p_table: 'PO_ORD',
            p_cond: " set ischk='N',cfmdttm=null,replydttm=null,cfrmk=null"
          }
        }).then(res => {
          if (res.code === '200') {
            this.$toast.success('反确认成功')
            this.mainRow.ISCHK = 'N'
            this.mainRow.CFMDTTM = null
            this.mainRow.REPLYDTTM = null
            this.mainRow.CFMRMK = null
          } else {
            this.$toast.fail(res.result || '操作失败')
          }
        })
      }).catch(() => {})
    },

    // 日期选择确认
    onDateConfirm(value) {
      this.confirmForm.REPLYDTTM = this.formatDate(value)
      this.showDatePicker = false
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 图片预览
    previewImage(path) {
      if (!path) return
      if (this.isImage(path)) {
        this.previewImages = [path]
        this.previewVisible = true
      } else {
        // PDF打开新窗口
        window.open(path, '_blank')
      }
    },

    isImage(path) {
      if (!path) return false
      const ext = path.toLowerCase()
      return ext.match(/\.(jpg|jpeg|png|gif|bmp)$/)
    },

    // 状态显示
    getStfgType(stfg) {
      const map = { 'Y': 'success', 'P': 'warning', 'K': 'primary' }
      return map[stfg] || 'default'
    },

    getStfgText(stfg) {
      const map = { 'Y': '已审核', 'N': '未审核', 'P': '审核中', 'K': '已发起' }
      return map[stfg] || stfg
    }
  }
}
</script>

<style lang="scss" scoped>
.po-cfm-detail-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #F7F8FA;
}

.header-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: #F7F8FA;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 999;
  flex-shrink: 0;

  .header-left {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .header-title {
    flex: 1;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    color: #333333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-right {
    width: 40px;
  }
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  padding-top: 56px;
  padding-bottom: 80px;
}

.info-card {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .card-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #F0F0F0;

    .order-no {
      font-size: 18px;
      font-weight: 600;
      color: #235EFF;
    }
  }

  .info-section {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .section-label {
      font-size: 14px;
      font-weight: 600;
      color: #333333;
      margin-bottom: 12px;
      padding-left: 8px;
      border-left: 3px solid #235EFF;

      &.confirm-info {
        border-left-color: #1ECD84;
      }
    }

    .info-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .info-item {
        min-width: calc(50% - 6px);

        &.info-item-full {
          flex: 1 1 100%;
        }

        .info-label {
          display: block;
          font-size: 12px;
          color: #999999;
          margin-bottom: 4px;
        }

        .info-value {
          display: block;
          font-size: 14px;
          color: #333333;

          &.amount {
            color: #FF6B00;
            font-weight: 600;
          }

          &.date-highlight {
            color: #FF6B00;
          }

          &.remark {
            color: #666666;
            font-size: 13px;
          }
        }
      }
    }
  }
}

.detail-section {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #F0F0F0;

    .detail-title {
      font-size: 14px;
      font-weight: 600;
      color: #333333;
      padding-left: 8px;
      border-left: 3px solid #235EFF;
    }

    .detail-count {
      font-size: 12px;
      color: #999999;
    }
  }

  .detail-list {
    .detail-item {
      padding: 12px 0;
      border-bottom: 1px solid #F7F8FA;

      &:last-child {
        border-bottom: none;
      }

      .item-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;

        .item-index {
          width: 20px;
          height: 20px;
          background: #235EFF;
          color: #FFFFFF;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .item-code {
          font-size: 14px;
          font-weight: 600;
          color: #333333;
        }

        .item-name {
          font-size: 14px;
          color: #333333;
          flex: 1;
        }
      }

      .item-body {
        padding-left: 28px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px 16px;

        .item-row {
          min-width: calc(33.33% - 12px);

          .row-label {
            font-size: 12px;
            color: #999999;
            margin-right: 4px;
          }

          .row-value {
            font-size: 13px;
            color: #333333;

            &.qty {
              color: #235EFF;
              font-weight: 600;
            }

            &.amount {
              color: #FF6B00;
              font-weight: 600;
            }
          }
        }
      }
    }
  }
}

.attach-section {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .attach-header {
    margin-bottom: 12px;

    .attach-title {
      font-size: 14px;
      font-weight: 600;
      color: #333333;
      padding-left: 8px;
      border-left: 3px solid #235EFF;
    }
  }

  .attach-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    .attach-item {
      cursor: pointer;

      .attach-preview {
        width: 80px;
        height: 80px;
        border-radius: 4px;
        overflow: hidden;
        border: 1px solid #E5E5E5;

        .van-image {
          display: block;
        }

        .attach-pdf {
          width: 100%;
          height: 100%;
          background: #F7F8FA;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          color: #999999;
          font-size: 12px;
        }
      }

      .attach-name {
        display: block;
        font-size: 12px;
        color: #666666;
        text-align: center;
        margin-top: 4px;
      }
    }
  }
}

.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 100px;
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #FFFFFF;
  border-top: 1px solid #EBEDF0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  z-index: 100;

  .footer-btn {
    width: 200px;
    height: 44px;
    border-radius: 22px;
    font-size: 16px;
    font-weight: 500;
    border: none;

    &.footer-btn-confirm {
      background: #235EFF;
      color: #FFFFFF;
    }

    &.footer-btn-cancel {
      background: #FF6B00;
      color: #FFFFFF;
    }
  }
}

.confirm-popup {
  background: #FFFFFF;
  border-radius: 16px 16px 0 0;

  .popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #EBEDF0;

    .popup-title {
      font-size: 18px;
      font-weight: 600;
      color: #333333;
    }

    .van-icon {
      cursor: pointer;
    }
  }

  .popup-body {
    padding: 16px;
  }

  .popup-form-item {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .popup-label {
      display: block;
      font-size: 13px;
      color: #333333;
      font-weight: 500;
      margin-bottom: 8px;

      .required {
        color: #FF4000;
        margin-right: 2px;
      }
    }
  }

  .popup-footer {
    display: flex;
    gap: 12px;
    padding: 16px;
    border-top: 1px solid #EBEDF0;

    .popup-btn {
      flex: 1;
      height: 44px;
      border-radius: 8px;
      font-size: 16px;

      &.popup-btn-cancel {
        background: #F0F0F0;
        color: #666666;
      }

      &.popup-btn-submit {
        background: #235EFF;
        color: #FFFFFF;
        border: none;
      }
    }
  }
}

::v-deep .van-field {
  padding: 10px 12px;
  background: #F7F8FA;
  border-radius: 8px;

  .van-field__control {
    font-size: 14px;
    color: #333333;

    &::placeholder {
      color: #999999;
    }
  }
}
</style>
